import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import DialogueBase from './DialogueBase';
import Button from '../Button';
import { connect } from 'react-redux';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import { ReviewAPI } from '../../api';
import SignUpCTA from './SignUpCTA';
import HelpIcon from '@material-ui/icons/Help';
import { Link } from 'react-router-dom';
import ReviewForm from '../forms/ReviewForm';

const alreadyReviewedStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    padding: 0,
  },
  section: {
    width: '100%',
    display: 'flex',
    flexWrap: 'nowrap',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginBottom: '2rem',
  },
  header: {
    padding: 0,
  },
  icon: {
    color: theme.palette.common.tealish,
    marginRight: '1rem',
  },
}));

const AlreadyReviewed = ({onClose, itemType}) => {
  const classes = alreadyReviewedStyles();
  return (
    <div className={classes.root}>
      <div className={classes.section}>
        <CheckCircleOutlineIcon fontSize="large" className={classes.icon}/>
        <Typography variant="h2" color="primary" className={classes.header}>
          You have already reviewed this {itemType}!
        </Typography>
      </div>
      <div className={classes.section}>
        <Typography variant="body2">
          You can only review each {itemType} once. If you would like to ammend your review please contact us.
        </Typography>
      </div>
      <div className={classes.section}>
        <Button label="Back to site" cssStyle="large" aria-label="Back to site" onClick={onClose}/>
      </div>
    </div>
  );
};

AlreadyReviewed.propTypes = {
  onClose: PropTypes.func.isRequired,
  itemType: PropTypes.string.isRequired,
};

const reviewCompletedStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    padding: 0,
  },
  section: {
    width: '100%',
    display: 'flex',
    flexWrap: 'nowrap',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginBottom: '2rem',
  },
  header: {
    padding: 0,
  },
  icon: {
    color: theme.palette.common.tealish,
    marginRight: '1rem',
  },
}));

const ReviewCompleted = ({onClose}) => {
  const classes = reviewCompletedStyles();
  return (
    <div className={classes.root}>
      <div className={classes.section}>
        <CheckCircleOutlineIcon fontSize="large" className={classes.icon}/>
        <Typography variant="h2" color="primary" className={classes.header}>
          Thank you for your review!
        </Typography>
      </div>
      <div className={classes.section}>
        <Typography variant="body2">
          We double-check all reviews to make sure we meet quality and compliance standards. This may take up to 48 hours so please be patient. Once approved, your review will be live on Clickability!
        </Typography>
      </div>
      <div className={classes.section}>
        <Button label="Back to site" cssStyle="large" aria-label="Back to site" onClick={onClose}/>
      </div>
    </div>
  );
};

ReviewCompleted.propTypes = {
  onClose: PropTypes.func.isRequired,
};


const newReviewStyles = makeStyles(theme => ({
  icon: {
    color: theme.palette.common.tealish,
    marginRight: '10px',
  },
  help: {
    width: '100%',
    padding: '0.5rem 0',
    display: 'flex',
    wrap: 'nowrap',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  link: {
    textDecoration: 'none',
    color: theme.palette.primary.main,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

const NewReview = ({onClose, providerUuid, itemType, itemUuid, itemName}) => {
  const classes = newReviewStyles();
  const [activeStep, setActiveStep] = React.useState(0);
  const handleCompletion = () => {
    setActiveStep(1);
  };
  return (
    <div className={classes.root}>
      {activeStep === 0 ? (
        <React.Fragment>
          <div className={classes.help}>
            <HelpIcon className={classes.icon}/>
            <Typography variant="body2">
              For help on how to write a great review, check out <Link className={classes.link} to="/review">our handy guide</Link>.
            </Typography>
          </div>
          <ReviewForm
            onClose={onClose}
            completedAction={handleCompletion}
            providerUuid={providerUuid}
            itemType={itemType}
            itemUuid={itemUuid}
            itemName={itemName}
          />
        </React.Fragment>
      ) : null}
      {activeStep === 1 ? (
        <ReviewCompleted onClose={onClose} />
      ) : null}
    </div>
  );
};

NewReview.propTypes = {
  onClose: PropTypes.func.isRequired,
  providerUuid: PropTypes.string.isRequired,
  itemType: PropTypes.string.isRequired,
  itemUuid: PropTypes.string.isRequired,
  itemName: PropTypes.string.isRequired,
};


const WriteReview = ({open, onClose, isAuthenticated, user, providerUuid, itemType, itemUuid, itemName}) => {
  const loading = React.useRef(true);
  const [alreadyReviewed, setAlreadyReviewed] = React.useState(false);
  const checkIfReviewed = async () => {
    try {
      const { payload } = await ReviewAPI.alreadyReviewed(itemType, itemUuid);
      return payload;
    } catch (err) {
      console.log(err) // eslint-disable-line
    }
  };

  React.useEffect(() => {
    loading.current = true;
    if (open && isAuthenticated && !user.is_provider) {
      checkIfReviewed()
        .then((result) => {
          if (loading.current) {
            setAlreadyReviewed(result.reviewed);
          }
        });
    }
    return () => { loading.current = false; };
  }, [open, isAuthenticated]); // eslint-disable-line

  if (!isAuthenticated) {
    return (<SignUpCTA open={open} onClose={onClose} />);
  }

  const isWrongUserType = user.is_provider;
  const title = `Write a review for ${itemName}`;

  return (
    <DialogueBase
      open={open}
      onClose={onClose}
      title={title}
      isWrongUserType={isWrongUserType}
      correctUserType="participant"
    >
      {alreadyReviewed ? (
        <AlreadyReviewed
          onClose={onClose}
          itemType={itemType}
        />
      ) : (
        <NewReview
          onClose={onClose}
          providerUuid={providerUuid}
          itemType={itemType}
          itemUuid={itemUuid}
          itemName={itemName}
        />
      )}
    </DialogueBase>
  );
};

WriteReview.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  user: PropTypes.object,
  providerUuid: PropTypes.string.isRequired,
  itemType: PropTypes.string.isRequired,
  itemUuid: PropTypes.string.isRequired,
  itemName: PropTypes.string.isRequired,
};


const mapStateToProps = state => {
  return {
    isAuthenticated: state.auth.isAuthenticated,
    user: state.auth.profile,
  };
};

export default connect(mapStateToProps)(WriteReview);
